import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Button,
  Typography,
  Chip,
  Avatar,
  Tooltip,
  TablePagination,
  Skeleton,
  Card,
  CardContent
} from '@mui/material';
import {
  Business as BusinessIcon,
  Launch as LaunchIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Star as StarIcon,
  Image as ImageIcon,
  Build as ServiceIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { amDesignColors } from '../../themes/amDesignSystem';

// 🎨 Import our new components
import KeywordBubbles from './KeywordBubbles';
import PostsIndicator from './PostsIndicator';

// 🎨 Enterprise-Grade Styled Components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'auto', // Changed from 'hidden' to 'auto' for scrolling
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  '& .MuiTableCell-head': {
    fontWeight: 600,
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    borderBottom: `2px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.2s ease-in-out',
  },
  '&:last-child td': {
    border: 0,
  },
  '& .MuiTableCell-root': {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const StatusChip = styled(Chip)(({ theme, variant }) => {
  const variants = {
    green: {
      backgroundColor: theme.palette.success.light,
      color: theme.palette.success.contrastText,
      fontWeight: 'bold',
    },
    yellow: {
      backgroundColor: theme.palette.warning.light,  
      color: theme.palette.warning.contrastText,
      fontWeight: 'bold',
    },
    red: {
      backgroundColor: theme.palette.error.light,
      color: theme.palette.error.contrastText,
      fontWeight: 'bold',
    },
  };
  
  return {
    ...variants[variant],
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.75rem',
    height: '24px',
  };
});

const TrendIcon = ({ trend }) => {
  const props = { sx: { fontSize: 16 } };
  switch (trend) {
    case 'improving': return <TrendingUpIcon color="success" {...props} />;
    case 'declining': return <TrendingDownIcon color="error" {...props} />;
    case 'stable': return <TrendingFlatIcon color="info" {...props} />;
    default: return null;
  }
};

const AgencyDataTable = ({ 
  data = [], 
  loading = false, 
  pagination = {}, 
  onSortChange, 
  onPageChange, 
  sortBy = '', 
  sortOrder = 'asc' 
}) => {
  const navigate = useNavigate();

  const handleSort = (field) => {
    const isAsc = sortBy === field && sortOrder === 'asc';
    onSortChange(field, isAsc ? 'desc' : 'asc');
  };

  const handleViewBusiness = (customer) => {
    navigate(`/posts/${customer.googleLocationId}`);
  };

  const handleOptimizeBusiness = (customer) => {
    navigate(`/optimization/${customer.businessId}`);
  };

  const handleManagePosts = (customer) => {
    navigate(`/posts/${customer.businessId}`);
  };

  const columns = [
    {
      id: 'businessName',
      label: 'Unternehmen',
      sortable: true,
      minWidth: 250,
      render: (customer) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ 
            bgcolor: 'primary.main', 
            width: 40, 
            height: 40,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
          }}>
            <BusinessIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ 
              fontWeight: 600,
              fontSize: '0.95rem',
              color: 'text.primary' 
            }}>
              {customer.businessName}
            </Typography>
            {customer.businessAddress?.city && (
              <Typography variant="caption" color="text.secondary">
                📍 {customer.businessAddress.city}
                {customer.businessAddress.postalCode && `, ${customer.businessAddress.postalCode}`}
              </Typography>
            )}
          </Box>
        </Box>
      )
    },
    {
      id: 'keywords',
      label: 'Keywords',
      sortable: true,
      minWidth: 220,
      align: 'left',
      render: (customer) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
              {customer.keywordCount || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Keywords
            </Typography>
          </Box>
          <KeywordBubbles 
            keywordIndicators={customer.keywordIndicators || []}
            totalKeywords={customer.keywordCount || 0}
          />
        </Box>
      )
    },
    {
      id: 'publishedPosts',
      label: 'Veröffentlicht',
      sortable: true,
      minWidth: 120,
      align: 'center',
      render: (customer) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            {customer.publishedPosts || 0}
          </Typography>
        </Box>
      )
    },
    {
      id: 'scheduledPosts',
      label: 'Geplant',
      sortable: true,
      minWidth: 100,
      align: 'center',
      render: (customer) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: 'info.main' }}>
            {customer.scheduledPosts || 0}
          </Typography>
        </Box>
      )
    },
    {
      id: 'averageRating',
      label: 'Bewertungen',
      sortable: true,
      minWidth: 150,
      align: 'center',
      render: (customer) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StarIcon sx={{ fontSize: 18, color: '#FFC107' }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {(customer.averageRating || 0).toFixed(1)}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {customer.reviewCount || 0} Reviews
          </Typography>
        </Box>
      )
    },
    {
      id: 'mediaCount',
      label: 'Medien',
      sortable: true,
      minWidth: 100,
      align: 'center',
      render: (customer) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <ImageIcon sx={{ fontSize: 18, color: '#9C27B0' }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {customer.mediaCount || 0}
          </Typography>
        </Box>
      )
    },
    {
      id: 'serviceCount',
      label: 'Services',
      sortable: true,
      minWidth: 100,
      align: 'center',
      render: (customer) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <ServiceIcon sx={{ fontSize: 18, color: '#FF5722' }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {customer.serviceCount || 0}
          </Typography>
        </Box>
      )
    },
    {
      id: 'improvementScore',
      label: 'Ranking Trend',
      sortable: true,
      minWidth: 160,
      align: 'center',
      render: (customer) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendIcon trend={customer.improvementTrend} />
            {customer.avgRanking && (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Ø {customer.avgRanking.toFixed(1)}
              </Typography>
            )}
          </Box>
          {customer.improvementScore !== 0 && (
            <Typography
              variant="caption"
              color={customer.improvementScore > 0 ? "success.main" : "error.main"}
            >
              {customer.improvementScore > 0 ? '+' : ''}{customer.improvementScore}
            </Typography>
          )}
        </Box>
      )
    },
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      minWidth: 100,
      align: 'center',
      render: (customer) => (
        <StatusChip 
          variant={customer.urgencyStatus}
          label={
            customer.urgencyStatus === 'green' ? 'Gut' :
            customer.urgencyStatus === 'yellow' ? 'Achtung' : 'Kritisch'
          }
        />
      )
    },
    {
      id: 'actions',
      label: 'Aktionen',
      sortable: false,
      minWidth: 120,
      align: 'right',
      render: (customer) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Business anzeigen">
            <IconButton
              size="small"
              onClick={() => handleViewBusiness(customer)}
              sx={{ 
                bgcolor: 'primary.light',
                color: 'primary.contrastText',
                '&:hover': {
                  bgcolor: 'primary.main',
                },
              }}
            >
              <LaunchIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  const LoadingSkeleton = ({ rows = 5 }) => (
    <>
      {[...Array(rows)].map((_, index) => (
        <TableRow key={index}>
          {columns.map((column) => (
            <TableCell key={column.id} align={column.align}>
              <Skeleton variant="rectangular" height={20} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );

  const EmptyState = () => (
    <TableRow>
      <TableCell colSpan={columns.length} align="center" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center' }}>
          <BusinessIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Keine Unternehmen gefunden
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Starten Sie eine neue Aggregation oder passen Sie Ihre Filter an.
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  console.log('🔍 AgencyDataTable Debug:', {
    dataLength: data.length,
    sortBy,
    sortOrder,
    paginationTotal: pagination?.total,
    paginationPage: pagination?.page
  });

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <StyledTableContainer 
        sx={{ 
          maxHeight: '70vh',
          minHeight: '400px',
          overflowY: 'auto'
        }}
      >
        <Table stickyHeader>
          <StyledTableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={sortBy === column.id}
                      direction={sortBy === column.id ? sortOrder : 'asc'}
                      onClick={() => handleSort(column.id)}
                      sx={{ 
                        '& .MuiTableSortLabel-icon': {
                          fontSize: '1rem'
                        }
                      }}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {loading ? (
              <LoadingSkeleton />
            ) : data.length === 0 ? (
              <EmptyState />
            ) : data && data.length > 0 ? (
              data.map((customer, index) => (
                <StyledTableRow
                  key={customer.businessId}
                  sx={{
                    cursor: 'pointer'
                  }}
                  onClick={() => handleViewBusiness(customer)}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      onClick={(e) => {
                        // Don't trigger row click for action buttons
                        if (column.id === 'actions') {
                          e.stopPropagation();
                        }
                      }}
                    >
                      {column.render ? column.render(customer) : customer[column.id]}
                    </TableCell>
                  ))}
                </StyledTableRow>
              ))
            ) : (
              <EmptyState />
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>

      {pagination && (
        <TablePagination
          component="div"
          count={pagination.total || 0}
          page={(pagination.page || 1) - 1}
          onPageChange={(e, newPage) => onPageChange(newPage + 1)}
          rowsPerPage={pagination.pageSize || 25}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onRowsPerPageChange={(e) => onPageChange(1, parseInt(e.target.value))}
          labelRowsPerPage="Zeilen pro Seite:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} von ${count !== -1 ? count : `mehr als ${to}`}`
          }
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            '& .MuiTablePagination-toolbar': {
              minHeight: 56
            }
          }}
        />
      )}
    </Paper>
  );
};

export default AgencyDataTable;